// src/context/AppContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
    Student, Group, Lesson,
    CreateStudentDto, UpdateStudentDto,
    CreateGroupDto, CreateLessonDto, UpdateLessonSeriesDto,
    CreateStudentResponse, Invitation,   // ← добавить
} from '../types';
import { studentsApi } from '../api/students';
import { groupsApi } from '../api/groups';
import { lessonsApi } from '../api/lessons';
import { useAuth } from './AuthContext';
import { isTeacherOrAdmin, isStudent } from '../utils/roleUtils';

interface AppContextType {
    students: Student[];
    groups: Group[];
    lessons: Lesson[];
    loading: boolean;
    error: string | null;

    // Ученики
    loadStudents: () => Promise<void>;
    loadAllStudents: () => Promise<void>;
    addStudent: (student: CreateStudentDto) => Promise<CreateStudentResponse>;
    regenerateInvitation: (studentId: number) => Promise<Invitation>;
    updateStudent: (id: number, data: UpdateStudentDto) => Promise<void>;
    deactivateStudent: (id: number) => Promise<void>;
    activateStudent: (id: number) => Promise<void>;
    deleteStudent: (id: number) => Promise<void>;


    // Группы
    loadGroups: () => Promise<void>;
    addGroup: (group: CreateGroupDto) => Promise<void>;
    updateGroup: (id: number, data: Partial<CreateGroupDto>) => Promise<void>;
    deleteGroup: (id: number, isNeedRemoveLessons?: boolean) => Promise<void>;
    addStudentToGroup: (groupId: number, studentId: number) => Promise<void>;
    removeStudentFromGroup: (groupId: number, studentId: number) => Promise<void>;

    // Уроки
    loadLessons: () => Promise<void>;
    addLesson: (lesson: CreateLessonDto) => Promise<Lesson | Lesson[] | any>;
    updateLesson: (id: number, data: Partial<CreateLessonDto>) => Promise<void>;
    deleteLesson: (id: number) => Promise<void>;
    updateLessonSeries: (seriesId: number, data: UpdateLessonSeriesDto) => Promise<void>;
    deleteLessonSeries: (seriesId: number, onlyFuture?: boolean) => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [students, setStudents] = useState<Student[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { user, isAuthenticated, loading: authLoading } = useAuth();

    // ---- ЗАГРУЗКА ДАННЫХ ----

    // Ученики (только для TEACHER/ADMIN)
    const loadStudents = useCallback(async () => {
        if (!isTeacherOrAdmin(user)) return;
        try {
            const response = await studentsApi.getAll();
            if (response.success && response.data) {
                setStudents(response.data);
            } else {
                setError(response.error || 'Ошибка загрузки учеников');
            }
        } catch (err) {
            console.error('Ошибка загрузки учеников:', err);
            throw err;
        }
    }, [user]);

    const loadAllStudents = useCallback(async () => {
        if (!isTeacherOrAdmin(user)) return;
        try {
            const response = await studentsApi.getAllWithInactive();
            if (response.success && response.data) {
                setStudents(response.data);
            } else {
                setError(response.error || 'Ошибка загрузки всех учеников');
            }
        } catch (err) {
            console.error('Ошибка загрузки всех учеников:', err);
            throw err;
        }
    }, [user]);

    // Группы (для всех — студент тоже может видеть название своей группы)
    const loadGroups = useCallback(async () => {
        try {
            const response = await groupsApi.getAll();
            if (response.success && response.data) {
                setGroups(response.data);
            } else {
                setError(response.error || 'Ошибка загрузки групп');
            }
        } catch (err) {
            console.error('Ошибка загрузки групп:', err);
            throw err;
        }
    }, []);

    // Уроки — разная логика по роли
    const loadLessons = useCallback(async () => {
        try {
            if (isStudent(user) && user?.studentId) {
                // STUDENT — используем специальный эндпоинт /for-student/lessons/student/{id}
                const response = await lessonsApi.getForStudent(user.studentId);
                if (response.success && response.data) {
                    setLessons(response.data);
                } else {
                    setError(response.error || 'Ошибка загрузки уроков');
                }
            } else if (isTeacherOrAdmin(user)) {
                // TEACHER/ADMIN — все уроки
                const response = await lessonsApi.getAll();
                if (response.success && response.data) {
                    setLessons(response.data);
                } else {
                    setError(response.error || 'Ошибка загрузки уроков');
                }
            } else {
                setLessons([]);
            }
        } catch (err) {
            console.error('Ошибка загрузки уроков:', err);
            throw err;
        }
    }, [user]);

    // Загрузка всех данных в зависимости от роли
    const loadAllData = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        setError(null);

        try {
            if (isTeacherOrAdmin(user)) {
                await Promise.all([loadStudents(), loadGroups(), loadLessons()]);
            } else if (isStudent(user)) {
                // ← СТУДЕНТ: только уроки
                await loadLessons();
            }
        } catch (err) {
            setError('Ошибка загрузки данных');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [user, loadStudents, loadGroups, loadLessons]);

    // Загрузка при входе/смене пользователя
    useEffect(() => {
        if (isAuthenticated && !authLoading && user) {
            loadAllData();
        }
    }, [isAuthenticated, authLoading, user, loadAllData]);

    // СБРОС данных при logout
    useEffect(() => {
        if (!isAuthenticated) {
            setStudents([]);
            setGroups([]);
            setLessons([]);
            setError(null);
        }
    }, [isAuthenticated]);

    // ---- CRUD УЧЕНИКОВ ----

    const addStudent = async (student: CreateStudentDto): Promise<CreateStudentResponse> => {
        try {
            const response = await studentsApi.create(student);
            if (response.success && response.data) {
                // data = CreateStudentResponse
                setStudents(prev => [...prev, response.data!.student]);
                return response.data;
            } else {
                setError(response.error || 'Ошибка создания ученика');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка создания ученика:', err);
            throw err;
        }
    };

// Также добавить метод для перегенерации приглашения
    const regenerateInvitation = async (studentId: number): Promise<Invitation> => {
        try {
            const response = await studentsApi.regenerateInvitation(studentId);
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.error || 'Ошибка генерации ссылки');
            }
        } catch (err) {
            console.error('Ошибка генерации ссылки:', err);
            throw err;
        }
    };

    const updateStudent = async (id: number, data: UpdateStudentDto) => {
        try {
            const response = await studentsApi.update(id, data);
            if (response.success && response.data) {
                setStudents(prev => prev.map(s => s.id === id ? response.data! : s));
                // Если деактивировали — обновляем группы и уроки
                if (data.isActive === false) {
                    await loadGroups();
                    await loadLessons();
                }
            } else {
                setError(response.error || 'Ошибка обновления ученика');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка обновления ученика:', err);
            throw err;
        }
    };

    const deactivateStudent = async (id: number) => {
        try {
            const response = await studentsApi.deactivate(id);
            if (response.success && response.data) {
                setStudents(prev => prev.map(s => s.id === id ? response.data! : s));
                await loadGroups();
                await loadLessons();
            } else {
                setError(response.error || 'Ошибка деактивации ученика');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка деактивации ученика:', err);
            throw err;
        }
    };

    const activateStudent = async (id: number) => {
        try {
            const response = await studentsApi.activate(id);
            if (response.success && response.data) {
                setStudents(prev => prev.map(s => s.id === id ? response.data! : s));
                await loadGroups();
                await loadLessons();
            } else {
                setError(response.error || 'Ошибка активации ученика');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка активации ученика:', err);
            throw err;
        }
    };


    // ---- CRUD ГРУПП ----

    const addGroup = async (group: CreateGroupDto) => {
        try {
            const response = await groupsApi.create(group);
            if (response.success && response.data) {
                setGroups(prev => [...prev, response.data!]);
                if (group.studentIds && group.studentIds.length > 0) {
                    await loadStudents();
                }
            } else {
                setError(response.error || 'Ошибка создания группы');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка создания группы:', err);
            throw err;
        }
    };

    const updateGroup = async (id: number, data: Partial<CreateGroupDto>) => {
        try {
            const response = await groupsApi.update(id, data);
            if (response.success && response.data) {
                setGroups(prev => prev.map(g => g.id === id ? response.data! : g));
                await loadStudents();
            } else {
                setError(response.error || 'Ошибка обновления группы');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка обновления группы:', err);
            throw err;
        }
    };

    const deleteGroup = async (id: number, isNeedRemoveLessons?: boolean) => {
        try {
            const response = await groupsApi.delete(id, isNeedRemoveLessons);
            if (response.success) {
                setGroups(prev => prev.filter(g => g.id !== id));
                await loadStudents();
                if (isNeedRemoveLessons) {
                    await loadLessons();
                }
            } else {
                setError(response.error || 'Ошибка удаления группы');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка удаления группы:', err);
            throw err;
        }
    };

    const addStudentToGroup = async (groupId: number, studentId: number) => {
        try {
            const response = await groupsApi.addStudent(groupId, studentId);
            if (response.success && response.data) {
                setGroups(prev => prev.map(g => g.id === groupId ? response.data! : g));
                await loadStudents();
            } else {
                setError(response.error || 'Ошибка добавления ученика в группу');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка добавления ученика в группу:', err);
            throw err;
        }
    };

    const removeStudentFromGroup = async (groupId: number, studentId: number) => {
        try {
            const response = await groupsApi.removeStudent(groupId, studentId);
            if (response.success && response.data) {
                setGroups(prev => prev.map(g => g.id === groupId ? response.data! : g));
                await loadStudents();
            } else {
                setError(response.error || 'Ошибка удаления ученика из группы');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка удаления ученика из группы:', err);
            throw err;
        }
    };

    // ---- CRUD УРОКОВ ----

    const addLesson = async (lesson: CreateLessonDto) => {
        try {
            const response = await lessonsApi.create(lesson);
            if (response.success && response.data) {
                const data = response.data as any;
                if (data.lessons && Array.isArray(data.lessons)) {
                    setLessons(prev => [...prev, ...data.lessons]);
                    return data;
                }
                setLessons(prev => [...prev, data]);
                return data;
            } else {
                setError(response.error || 'Ошибка создания урока');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка создания урока:', err);
            throw err;
        }
    };

    const updateLesson = async (id: number, data: Partial<CreateLessonDto>) => {
        try {
            const response = await lessonsApi.update(id, data);
            if (response.success && response.data) {
                const result = response.data as any;
                if (result.lessons && Array.isArray(result.lessons)) {
                    const updatedIds = new Set(result.lessons.map((l: Lesson) => l.id));
                    setLessons(prev => [
                        ...prev.filter(l => !updatedIds.has(l.id) && l.id !== id),
                        ...result.lessons,
                    ]);
                } else {
                    setLessons(prev => prev.map(l => l.id === id ? result : l));
                }
            } else {
                setError(response.error || 'Ошибка обновления урока');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка обновления урока:', err);
            throw err;
        }
    };

    const deleteLesson = async (id: number) => {
        try {
            const response = await lessonsApi.delete(id);
            if (response.success) {
                setLessons(prev => prev.filter(l => l.id !== id));
            } else {
                setError(response.error || 'Ошибка удаления урока');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка удаления урока:', err);
            throw err;
        }
    };

    const updateLessonSeries = async (seriesId: number, data: UpdateLessonSeriesDto) => {
        try {
            const response = await lessonsApi.updateSeries(seriesId, data);
            if (response.success && response.data) {
                const result = response.data as any;
                if (result.lessons && Array.isArray(result.lessons)) {
                    const updatedIds = new Set(result.lessons.map((l: Lesson) => l.id));
                    setLessons(prev => [
                        ...prev.filter(l => !updatedIds.has(l.id)),
                        ...result.lessons,
                    ]);
                }
            } else {
                setError(response.error || 'Ошибка обновления серии');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка обновления серии:', err);
            throw err;
        }
    };

    const deleteLessonSeries = async (seriesId: number, onlyFuture: boolean = true) => {
        try {
            const response = await lessonsApi.deleteSeries(seriesId, onlyFuture);
            if (response.success) {
                await loadLessons();
            } else {
                setError(response.error || 'Ошибка удаления серии');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка удаления серии:', err);
            throw err;
        }
    };

    const deleteStudent = async (id: number) => {
        try {
            const response = await studentsApi.delete(id);
            if (response.success) {
                // Удаляем из локального состояния
                setStudents(prev => prev.filter(s => s.id !== id));

                // Также убираем из групп
                setGroups(prev => prev.map(g => ({
                    ...g,
                    studentIds: g.studentIds.filter(sid => sid !== id),
                })));

                // И удаляем все его уроки из локального состояния
                setLessons(prev => prev.filter(l => l.studentId !== id));
            } else {
                setError(response.error || 'Ошибка удаления ученика');
                throw new Error(response.error);
            }
        } catch (err) {
            console.error('Ошибка удаления ученика:', err);
            throw err;
        }
    };

    const value: AppContextType = {
        students,
        groups,
        lessons,
        loading,
        error,
        loadStudents,
        loadAllStudents,
        addStudent,
        updateStudent,
        deactivateStudent,
        activateStudent,
        loadGroups,
        addGroup,
        updateGroup,
        deleteGroup,
        addStudentToGroup,
        removeStudentFromGroup,
        loadLessons,
        addLesson,
        updateLesson,
        deleteLesson,
        updateLessonSeries,
        deleteLessonSeries,
        deleteStudent,
        regenerateInvitation,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};